import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGnrPeriodLabel, gnrStateEnum } from '../../../../constants';
import { useBulkUpdateGnrRequestsMutation } from '../../../../hooks/mutations/useGnrRequestMutation';
import { useGnrSuppliersQuery } from '../../../../hooks/queries/useGnrSuppliersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { GnrRequest } from '../../../../services/apiSLTP/sites/gnrRequests';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    open: boolean;
    handleClose: () => void;
    gnrRequests: GnrRequest[];
    state: number;
    refetch: () => void;
}

const ConfirmGnrRequestModal: React.FC<ModalProps> = ({ open = false, handleClose = () => {}, gnrRequests, state, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataGnrSuppliers, isLoading: isGnrSuppliersLoading } = useGnrSuppliersQuery({ queries: { active: true } });

    const { mutate: updateGnrRequests } = useBulkUpdateGnrRequestsMutation({ onSettled: refetch });

    const [selectedGnrSupplier, setSelectedSupplier] = useState<OptionType | null>(null);
    const [sendEmail, setSendEmail] = useState(true);
    const [confirmRequests, setConfirmRequests] = useState(state === gnrStateEnum.REQUESTED ? true : false);

    const generateTableText = () => {
        const selectedData = gnrRequests;
        let tableText = '';
        selectedData?.forEach((gnrRequest) => {
            tableText += `- ${capitalizeFirstLetter(
                new Date(gnrRequest.requestDate).toLocaleDateString(t('localeDate'), {
                    weekday: 'long',
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric',
                })
            )} ${capitalizeFirstLetter(t(getGnrPeriodLabel(gnrRequest.requestPeriod)))} ${gnrRequest.requestVolume}L de GNR\n${gnrRequest.siteId.address}\nContact : ${formatName(gnrRequest.requesterId.firstName, gnrRequest.requesterId.lastName)}${gnrRequest.requesterId.phoneNumber ? ` (${gnrRequest.requesterId.phoneNumber})` : ''}\n(Ref: ${gnrRequest._id})\n\n`;
        });

        return tableText;
    };

    const handleOpenEmailClient = () => {
        const tableText = generateTableText();
        const subject = encodeURIComponent(`Livraison de GNR`);
        const body = encodeURIComponent(
            `Bonjour,\n\nMerci de livrer :\n${tableText}\nEn attente de votre confirmation.\n\n${new Date().toLocaleDateString(t('localeDate'), {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })}`
        );

        const recipientEmail = dataGnrSuppliers?.data.find((gnrSupplier) => gnrSupplier._id === selectedGnrSupplier?.value)?.email || '';
        const mailtoLink = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;

        window.location.href = mailtoLink;
    };

    const handleSend = () => {
        if (sendEmail) {
            handleOpenEmailClient();
        }
        if (confirmRequests) {
            if (!selectedGnrSupplier) return;
            updateGnrRequests({
                body: { supplierId: selectedGnrSupplier.value, gnrRequestIds: gnrRequests.map((gnrRequest) => gnrRequest._id) },
                queries: { withNotification: true },
            });
        }
    };

    return (
        <ModalBody
            open={open}
            handleClose={handleClose}
            title={t('gnrRequest')}
            subtitle={t('confirmDelivery')}
            onSubmit={handleSend}
            disableSubmit={!selectedGnrSupplier?.value.length || (!sendEmail && !confirmRequests)}
        >
            <DropDown
                items={
                    dataGnrSuppliers?.data.map((gnrSupplier) => ({ label: gnrSupplier.name + ' - ' + gnrSupplier.area, value: gnrSupplier._id })) ||
                    []
                }
                selectedItem={selectedGnrSupplier}
                handleChange={(e, v) => setSelectedSupplier(v)}
                inputLabel={t('gnrSupplier')}
                required
                loading={isGnrSuppliersLoading}
            />
            <ToggleLine checked={sendEmail} onChange={(checked) => setSendEmail(checked)} title={capitalizeFirstLetter(t('prepareEmail'))} />
            {state === gnrStateEnum.REQUESTED && (
                <ToggleLine
                    checked={confirmRequests}
                    onChange={(checked) => setConfirmRequests(checked)}
                    title={capitalizeFirstLetter(t('confirmRequests'))}
                />
            )}
        </ModalBody>
    );
};

export default ConfirmGnrRequestModal;
